import { useEffect, useState, Fragment, useMemo, useRef, lazy, Suspense } from 'react';
import React from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPinIcon, GlobeAltIcon, PhoneIcon, EnvelopeIcon, UserGroupIcon, CalendarDaysIcon, EllipsisVerticalIcon, PencilSquareIcon, ExclamationTriangleIcon, SparklesIcon, ArrowTopRightOnSquareIcon, CheckCircleIcon, HeartIcon, ClipboardIcon, FlagIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { Dialog, Transition } from '@headlessui/react';
import { supabase } from '../lib/supabase';
import { getCampImage } from '../lib/images';
import { useSession } from '@supabase/auth-helpers-react';
import toast from 'react-hot-toast';
import type { Camp } from '../types/database';
import RotatingCampImages from '../components/RotatingCampImages';
import { Helmet } from 'react-helmet-async';
import { api } from '../lib/api';
import { track } from '../lib/track';
import SEO from '../components/SEO';
import CampMap from '../components/CampMap';
import { extractStreetAddress, addUtmParams } from '../lib/campUtils';
import { WarningAlert } from '../components/ui/Alert';
import CategoryTags from '../components/ui/CategoryTags';

// Lazy load admin overlay to prevent MDX editor from being bundled in SSR
const AdminCampOverlay = lazy(() => import('../components/AdminCampOverlay'));
import { useTheme } from '../lib/theme/index.js';
import ClaimCampCard from '../components/ClaimCampCard';
import PurchaseTeaser from '../components/PurchaseTeaser';
import PaymentSuccessModal from '../components/PaymentSuccessModal';
import EnrichmentModal from '../components/EnrichmentModal';
import PasteEnrichmentModal from '../components/PasteEnrichmentModal';
import type { ClaimableCamp } from '../data/campClaimSeedData';
import { useUserTypes } from '../hooks/useUserTypes';
import MarkdownRenderer from '../components/MarkdownRenderer';

export default function CampPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [heroImage, setHeroImage] = useState<string>('');
  const [logoImage, setLogoImage] = useState<string>('');
  const [selectedChild, setSelectedChild] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);
  
  // Admin actions state
  const [showAdminActions, setShowAdminActions] = useState(false);
  
  // Admin preview images toggle - per camp (stored in localStorage as Set of camp IDs)
  // Initialize as empty Set to avoid hydration mismatch (will be populated in useEffect)
  const [previewImagesCamps, setPreviewImagesCamps] = useState<Set<string>>(new Set());

  // Load preview images camps from localStorage after mount (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('admin_preview_images_camps');
      if (stored) {
        setPreviewImagesCamps(new Set(JSON.parse(stored)));
      }
    }
  }, []);

  // Check for preview token in URL
  const searchParams = new URLSearchParams(location.search);
  const previewToken = searchParams.get('preview');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ camp: Camp; skipForever: boolean } | null>(null);
  const [isEnrichmentModalOpen, setIsEnrichmentModalOpen] = useState(false);
  const [isPasteEnrichmentModalOpen, setIsPasteEnrichmentModalOpen] = useState(false);

  // Payment success modal state
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [successPlanName, setSuccessPlanName] = useState<string>('');

  // Check for payment success parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentSuccess = searchParams.get('payment_success');
    const plan = searchParams.get('plan');

    if (paymentSuccess === 'true' && plan) {
      setSuccessPlanName(plan);
      setShowPaymentSuccess(true);
      
      // Clean up URL parameters
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('payment_success');
      newSearchParams.delete('plan');
      
      const newUrl = `${location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`;
      navigate(newUrl, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // Check admin status to allow viewing inactive camps
  // Get user types from the user_types table FIRST
  const { isCampOwner, isParent, isAdmin: isAdminFromUserTypes } = useUserTypes();

  // Then check admin_users table
  const { data: isAdminFromTable } = useQuery(
    ['is-admin', session?.user?.id],
    async () => {
      if (!session?.user?.id) {
        return false;
      }
      
      const { data, error } = await supabase
        .rpc('check_is_admin', {
          user_id: session.user.id
        });
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return !!data;
    },
    {
      enabled: !!session?.user?.id,
      staleTime: 30000, // Cache for 30 seconds
      cacheTime: 60000, // Keep in cache for 1 minute
      retry: false
    }
  );

  // Combine both admin checks: either in admin_users table OR has 'admin' in user_types
  // Use useMemo to ensure proper dependency handling
  // IMPORTANT: Default to false during SSR to prevent hydration mismatches
  const isAdmin = useMemo(() => {
    // During SSR, always default to false to ensure consistent hydration
    if (typeof window === 'undefined') {
      return false;
    }
    return Boolean(isAdminFromTable || isAdminFromUserTypes);
  }, [isAdminFromTable, isAdminFromUserTypes]);

  // Extract slug from URL path
  const getSlugFromPath = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'camps' && pathParts.length >= 3) {
      // Remove 'camps' from the beginning and join the rest
      return pathParts.slice(2).join('/');
    }
    return null;
  };

  const effectiveSlug = getSlugFromPath();

  // Track if component has mounted to prevent hydration mismatches
  const [hasMounted, setHasMounted] = useState(false);
  
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // IMPORTANT: For SSR hydration, always use false for isAdmin in query key during initial render
  // This ensures server and client render the same initial data
  // After hydration, we can use the actual isAdmin value
  const queryIsAdmin = hasMounted ? isAdmin : false;

  // Fetch camp details
  const { data: camp, isLoading, error: campError } = useQuery(
    ['camp', effectiveSlug, queryIsAdmin],
    async () => {
      if (!effectiveSlug) {
        console.error('CampPage: No slug provided');
        throw new Error('No slug provided');
      }

      console.log('CampPage: Starting to fetch camp with slug:', effectiveSlug, 'isAdmin:', queryIsAdmin);
      try {
        const campData = await api.camps.getCampBySlug(effectiveSlug, !!queryIsAdmin);
        console.log('CampPage: Raw camp data received:', JSON.stringify(campData, null, 2));
        
        if (!campData) {
          console.error('CampPage: No camp data received');
          throw new Error('Camp not found');
        }

        // If the camp was found by ID or partial slug, update it with the new format
        if (!campData.slug || !campData.slug.includes('/')) {
          console.log('CampPage: Updating camp slug format...');
          const stateSlug = campData.state.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .trim();
          
          const citySlug = campData.city.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .trim();
          
          const nameSlug = campData.name.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .trim();
          
          const newSlug = `${stateSlug}/${citySlug}/${nameSlug}`;
          console.log('CampPage: New slug format:', newSlug);
          
          // Update the camp with the new slug
          const { data: updatedCamp, error: updateError } = await supabase
            .from('camps')
            .update({ slug: newSlug })
            .eq('id', campData.id)
            .select()
            .single();

          if (!updateError && updatedCamp) {
            console.log('CampPage: Camp updated with new slug:', updatedCamp);
            // Redirect to the new URL with the slug
            navigate(`/camps/${updatedCamp.slug}`, { replace: true });
            return updatedCamp;
          } else {
            console.error('CampPage: Error updating camp slug:', updateError);
          }
        }
        
        return campData;
      } catch (error) {
        console.error('CampPage: Error fetching camp:', error);
        throw error;
      }
    },
    {
      enabled: !!effectiveSlug, // Re-enable for both server and client
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      onError: (error) => {
        console.error('CampPage: Error in camp query:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error('CampPage: Full error details:', {
          error,
          message: errorMessage,
          slug: effectiveSlug,
          isAdmin: queryIsAdmin
        });
        
        // Don't redirect immediately - show error message first
        if (errorMessage.includes('not found') || errorMessage.includes('PGRST116')) {
          toast.error(`Camp not found: ${effectiveSlug}`);
        } else {
          toast.error(`Failed to load camp: ${errorMessage}`);
        }
        
        // Only redirect after a delay to let user see the error
        setTimeout(() => {
          navigate('/');
        }, 3000);
      },
      onSuccess: (data) => {
        console.log('CampPage: Query succeeded with data:', JSON.stringify(data, null, 2));
        // Update favorite status when camp data loads
        if (session?.user?.id && data?.camp_favorites) {
          const userFavorite = data.camp_favorites.find((fav: any) => fav.user_id === session.user.id);
          setIsFavorite(!!userFavorite);
        } else {
          setIsFavorite(false);
        }
      }
    }
  );

  // Update favorite status when camp data or session changes
  useEffect(() => {
    if (session?.user?.id && camp?.camp_favorites) {
      const userFavorite = camp.camp_favorites.find((fav: any) => fav.user_id === session.user.id);
      setIsFavorite(!!userFavorite);
    } else {
      setIsFavorite(false);
    }
  }, [camp, session?.user?.id]);

  // Track camp_viewed once per camp load (avoid double-fire on refetch)
  const campViewedFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!camp?.id || campViewedFiredRef.current === camp.id) return;
    campViewedFiredRef.current = camp.id;
    const category = (camp as Camp & { camp_category_relations?: Array<{ camp_categories?: { name: string } }> }).camp_category_relations?.[0]?.camp_categories?.name
      ?? (camp as Camp & { camp_categories?: Array<{ name: string }> }).camp_categories?.[0]?.name
      ?? undefined;
    track('camp_viewed', {
      camp_id: camp.id,
      camp_name: camp.name,
      city: camp.city,
      state: camp.state,
      category,
    });
  }, [camp?.id]);

  // Admin action handlers
  const handleEditCamp = () => {
    setIsEditModalOpen(true);
    setShowAdminActions(false);
  };

  const handleToggleActive = async () => {
    if (!camp) return;
    
    try {
      const { error } = await supabase
        .from('camps')
        .update({ active: !camp.active })
        .eq('id', camp.id);

      if (error) throw error;

      // Invalidate and refetch the camp data
      void queryClient.invalidateQueries(['camp', effectiveSlug, isAdmin]);
      
      toast.success(`Camp ${camp.active ? 'deactivated' : 'activated'} successfully`);
      setShowAdminActions(false);
    } catch (error) {
      console.error('Error toggling camp status:', error);
      toast.error('Failed to update camp status');
    }
  };

  const handleDeleteCamp = () => {
    if (!camp) return;
    setDeleteConfirmation({ camp, skipForever: true });
    setShowAdminActions(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation) return;

    const { camp: campToDelete, skipForever } = deleteConfirmation;
    
    try {
      // If skipForever is checked, add to skipped_camps table
      if (skipForever) {
        const { error: skipError } = await supabase
          .from('skipped_camps')
          .insert({
            name: campToDelete.name,
            website_url: campToDelete.website_url,
            slug: campToDelete.slug,
            reason: 'Deleted by admin'
          });

        if (skipError) {
          console.warn('Warning: Failed to add camp to skip list:', skipError);
        }
      }

      // Delete related records first (in order to avoid foreign key constraints)
      // Delete camp schedules (children's calendar entries)
      const { error: schedulesError } = await supabase
        .from('camp_schedules')
        .delete()
        .eq('camp_id', campToDelete.id);

      if (schedulesError) {
        console.warn('Warning: Failed to delete camp schedules:', schedulesError);
        // Continue - some schedules might not exist
      }

      // Delete camp favorites using RPC function (bypasses RLS)
      const { data: favoritesDeleted, error: favoritesError } = await supabase
        .rpc('admin_delete_camp_favorites', {
          camp_id_to_delete: campToDelete.id
        });

      if (favoritesError) {
        console.error('Error deleting camp favorites:', favoritesError);
        console.error('Favorites deletion details:', {
          campId: campToDelete.id,
          error: favoritesError
        });
        throw favoritesError;
      }

      console.log(`Deleted ${favoritesDeleted || 0} favorites for camp ${campToDelete.id}`);

      // Delete images from Supabase Storage before removing database records
      try {
        const { data: images } = await supabase
          .from('camp_images')
          .select('url')
          .eq('camp_id', campToDelete.id);
        
        if (images && images.length > 0) {
          // Extract file paths from URLs and delete from storage
          for (const image of images) {
            try {
              // Extract the path from the URL (e.g., "camp-images/campId/filename" from full URL)
              const urlParts = image.url.split('/');
              const storagePath = urlParts.slice(-2).join('/'); // Get campId/filename
              
              const { error: removeError } = await supabase.storage
                .from('camp-images')
                .remove([storagePath]);
              
              if (removeError) {
                console.warn(`Warning: Failed to delete storage file ${storagePath}:`, removeError);
              }
            } catch (storageError) {
              console.warn(`Warning: Failed to process storage file ${image.url}:`, storageError);
            }
          }
        }
      } catch (storageError) {
        console.warn('Warning: Failed to delete some storage files:', storageError);
        // Continue with deletion even if storage cleanup fails
      }

      const { error: imagesError } = await supabase
        .from('camp_images')
        .delete()
        .eq('camp_id', campToDelete.id);

      if (imagesError) throw imagesError;

      const { error: categoryError } = await supabase
        .from('camp_category_relations')
        .delete()
        .eq('camp_id', campToDelete.id);

      if (categoryError) throw categoryError;

      // Finally delete the camp
      const { error: campError } = await supabase
        .from('camps')
        .delete()
        .eq('id', campToDelete.id);

      if (campError) throw campError;

      const message = skipForever 
        ? 'Camp deleted and added to skip list to prevent future imports'
        : 'Camp deleted successfully';
      toast.success(message);
      
      setDeleteConfirmation(null);
      navigate('/admin');
    } catch (error) {
      console.error('Error deleting camp:', error);
      toast.error('Failed to delete camp');
    }
  };

  // Close admin actions when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showAdminActions) {
        setShowAdminActions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showAdminActions]);

  // Fetch children for calendar integration
  const { data: children = [] } = useQuery(
    ['children'],
    async () => {
      if (!session?.user?.id) return [];
      const { data, error } = await supabase
        .from('children')
        .select('id, user_id, name, date_of_birth, created_at, updated_at')
        .eq('user_id', session.user.id);
      if (error) throw error;
      return data;
    },
    {
      enabled: !!session?.user?.id
    }
  );

  // Fetch camp schedules for the current camp and user's children
  const { data: campSchedules = [] } = useQuery(
    ['camp-schedules', camp?.id, session?.user?.id],
    async () => {
      if (!session?.user?.id || !camp?.id) return [];
      const { data, error } = await supabase
        .from('camp_schedules')
        .select(`
          id,
          child_id,
          camp_id,
          start_date,
          end_date,
          event_year,
          created_at,
          updated_at,
          children (
            id,
            name
          )
        `)
        .eq('camp_id', camp.id)
        .in('child_id', children.map(child => child.id));
      if (error) throw error;
      return data;
    },
    {
      enabled: !!session?.user?.id && !!camp?.id && children.length > 0
    }
  );

  // Check if preview images are enabled for current camp (must be after camp is defined)
  const previewImagesEnabled = useMemo(() => {
    return camp ? previewImagesCamps.has(camp.id) : false;
  }, [camp, camp?.id, previewImagesCamps]);

  // Set images when camp data is loaded
  useEffect(() => {
    if (camp) {
      console.log('CampPage: Setting camp images for camp:', JSON.stringify(camp, null, 2));
      const photo = camp.camp_images?.find((img: { type: string }) => img.type === 'photo');
      const logo = camp.camp_images?.find((img: { type: string }) => img.type === 'logo');
      
      // Admin preview override: show all images to everyone regardless of display flags
      if (previewImagesEnabled) {
        if (photo) {
          console.log('CampPage: Setting hero image (admin override):', photo.url);
          setHeroImage(photo.url);
        } else {
          const defaultImage = getCampImage(camp);
          console.log('CampPage: Setting default hero image (admin override):', defaultImage);
          setHeroImage(defaultImage);
        }
        
        if (logo) {
          console.log('CampPage: Setting logo image (admin override):', logo.url);
          setLogoImage(logo.url);
        }
      } else {
        // Normal flow
        if (photo) {
          console.log('CampPage: Setting hero image:', photo.url);
          setHeroImage(photo.url);
        } else {
          const defaultImage = getCampImage(camp);
          console.log('CampPage: Setting default hero image:', defaultImage);
          setHeroImage(defaultImage);
        }

        if (logo && camp.display_logo) {
          console.log('CampPage: Setting logo image:', logo.url);
          setLogoImage(logo.url);
        }
      }
    }
  }, [camp, previewImagesEnabled]);

  // Determine if we should show premium sections (images, logos, schedule/pricing)
  // Show if: camp has plan_id (is paying) OR preview token matches OR admin preview override
  // Must be defined BEFORE any early returns to follow Rules of Hooks
  const shouldShowPremiumSections = useMemo(() => {
    if (!camp) return false;
    // Admin preview override - shows all premium sections to everyone
    if (previewImagesEnabled) {
      return true;
    }
    const isPaying = camp.plan_id !== null;
    const hasValidPreviewToken = previewToken && camp.preview_token && previewToken === camp.preview_token;
    return isPaying || hasValidPreviewToken;
  }, [camp, previewToken, previewImagesEnabled]);

  // Get photo images - show all if admin preview override is enabled, otherwise respect display_images flag
  // Must be defined BEFORE any early returns to follow Rules of Hooks
  const photoImages = useMemo(() => {
    if (!camp) return [];
    if (previewImagesEnabled) {
      // Admin override: show all photos to everyone
      return camp.camp_images?.filter((img: { type: string }) => img.type === 'photo') || [];
    }
    // Normal flow: respect display_images flag
    return (camp.display_images ? camp.camp_images?.filter((img: { type: string }) => img.type === 'photo') : []) || [];
  }, [camp, camp?.camp_images, camp?.display_images, previewImagesEnabled]);
  const additionalImages = photoImages;

  // Add debug logging for loading and error states
  useEffect(() => {
    console.log('CampPage: Page state:', {
      isLoading,
      hasCamp: !!camp,
      error: campError,
      slug: effectiveSlug,
      pathname: location.pathname,
      campData: camp ? JSON.stringify(camp, null, 2) : null
    });
  }, [isLoading, camp, campError, effectiveSlug, location.pathname]);

  const handleAddToCalendar = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!session) {
      toast((t) => (
        <span className="flex items-center gap-2">
          <span className="whitespace-nowrap">Please sign in to add camps to your kids schedule</span>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              navigate('/login');
            }}
            className="whitespace-nowrap bg-blue-500 text-white text-sm px-2 py-1 rounded hover:bg-blue-600 transition-colors"
          >
            Sign In
          </button>
        </span>
      ), {
        duration: 5000,
      });
      return;
    }

    if (!selectedChild || !startDate || !endDate) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      // Calculate event_year from start_date
      const startDateObj = new Date(startDate);
      const eventYear = startDateObj.getFullYear();
      const currentYear = new Date().getFullYear();
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time for accurate date comparison
      
      // Validation: allow dates in current year or future, or up to 30 days in the past for corrections
      const daysDiff = Math.floor((startDateObj.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      if (eventYear < currentYear) {
        toast.error(`Can't create camp schedules for ${eventYear}. Please select dates in ${currentYear} or later.`);
        return;
      }
      
      // Allow dates up to 30 days in the past for corrections
      if (daysDiff < -30) {
        const minDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        toast.error(`Can't create camp schedules more than 30 days in the past. Please select dates from ${minDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} or later.`);
        return;
      }

      const { error } = await supabase
        .from('camp_schedules')
        .insert({
          camp_id: camp?.id,
          child_id: selectedChild,
          start_date: startDate,
          end_date: endDate,
          event_year: eventYear
        });

      if (error) throw error;

      track('camp_added_to_schedule', {
        camp_id: camp?.id,
        child_id: selectedChild,
        persona: 'parent',
        event_year: eventYear,
        start_date: startDate,
        end_date: endDate
      });

      toast.success('Camp added to kids schedule');
      navigate('/kids-schedule');
    } catch (error) {
      console.error('Error adding camp to calendar:', error);
      toast.error('Failed to add camp to kids schedule');
    }
  };

  // Check if we should show loading state
  // During SSR, we want to show a loading state, but on client we want to show content
  const isServer = typeof window === 'undefined';
  const shouldShowLoading = isLoading && !isServer;
  
  // Extract camp name from URL for better SEO during loading
  const getCampNameFromUrl = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts.length >= 5) {
      // For URLs like /camps/florida/jacksonville/code-ninjas-summer-camps
      // pathParts[0] = "", pathParts[1] = "camps", pathParts[2] = "florida", 
      // pathParts[3] = "jacksonville", pathParts[4] = "code-ninjas-summer-camps"
      const campNamePart = pathParts[4]; // The camp name part
      return campNamePart
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return 'Camp';
  };
  
  const campNameFromUrl = getCampNameFromUrl();
  const getCityStateFromUrl = () => {
    const parts = location.pathname.split('/');
    const state = parts[2]?.replace(/-/g, ' ') || '';
    const city = parts[3]?.replace(/-/g, ' ') || '';
    return { city, state };
  };
  const { city: cityFromUrl, state: stateFromUrl } = getCityStateFromUrl();
  
  // Show loading state only if we don't have data and we're loading
  if (isLoading && !camp) {
    console.log('CampPage: Showing loading state');
    
    // During SSR, show a more SEO-friendly loading state
    if (typeof window === 'undefined') {
      return (
        <div className="min-h-screen">
          <SEO
            title={`${campNameFromUrl} - Summer Camp in ${cityFromUrl || 'Your City'}, ${stateFromUrl || 'Your State'} | CampWhiz`}
            description={`Learn about ${campNameFromUrl}. View details, activities, and contact info on CampWhiz.`}
            url={location.pathname}
          />
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">{campNameFromUrl}</h1>
              <p className="text-lg text-gray-600 mb-6">Summer camp in {cityFromUrl || 'your city'}, {stateFromUrl || 'your state'}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Camp Information</h2>
                  <p className="text-gray-600">Loading detailed camp information...</p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Activities & Programs</h2>
                  <p className="text-gray-600">Loading camp activities and programs...</p>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">About This Camp</h2>
                <p className="text-gray-600">Loading camp description and details...</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // On client side, show animated loading
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Loading {campNameFromUrl}</h1>
          <p className="text-gray-600">Please wait while we fetch the camp information...</p>
        </div>
      </div>
    );
  }

  if (campError) {
    console.log('CampPage: Showing error state:', campError);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">Error Loading Camp</h1>
          <p className="text-gray-600 mb-4">{campError instanceof Error ? campError.message : 'An error occurred'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (!camp) {
    console.log('CampPage: Showing camp not found state');
    return (
      <>
        <SEO
          title="Camp Not Found | CampWhiz"
          description="The camp you're looking for doesn't exist or has been removed."
          url={location.pathname}
        />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">Camp Not Found</h1>
            <p className="text-gray-600 mb-4">The camp you're looking for doesn't exist or has been removed.</p>
            <p className="text-sm text-gray-500 mb-4">URL: {location.pathname}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Return Home
            </button>
          </div>
        </div>
      </>
    );
  }

  console.log('CampPage: Rendering main camp content');

  // Prepare structured data for the camp page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Camp",
    "name": camp.name,
    "description": camp.description,
    "image": heroImage,
    "url": `https://campwhiz.com/camps/${camp.slug}`,
    "telephone": camp.phone,
    "email": camp.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": camp.address,
      "addressLocality": camp.city,
      "addressRegion": camp.state,
      "postalCode": camp.zip_code
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": camp.latitude,
      "longitude": camp.longitude
    },
    "openingHours": [
      `Mo-Fr ${camp.start_hour_1}-${camp.finish_hour_1}`,
      camp.start_hour_2 ? `Mo-Fr ${camp.start_hour_2}-${camp.finish_hour_2}` : null
    ].filter(Boolean).join("; "),
    "priceRange": camp.five_day_cost ? `$${camp.five_day_cost}` : undefined,
    "suitableForAge": {
      "@type": "QuantitativeValue",
      "minValue": camp.min_age,
      "maxValue": camp.max_age
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": camp.five_day_cost,
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "reviewCount": "100"
    }
  };

  // Show loading state while fetching camp data
  if (isLoading || !camp) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
        <SEO
          title="Loading Camp - CampWhiz"
          description="Loading camp details..."
          url="/camps/loading"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading camp details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if camp fetch failed
  if (campError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
        <SEO
          title="Camp Not Found - CampWhiz"
          description="The requested camp could not be found."
          url="/camps/error"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">Camp Not Found</h1>
            <p className="text-gray-600 mb-6">The camp you're looking for could not be found.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <SEO
        title={`${camp.name} - Summer Camp in ${camp.city}, ${camp.state} | CampWhiz`}
        description={camp.short_description || camp.description || `Discover ${camp.name}, a premier summer camp in ${camp.city}, ${camp.state}.${camp.min_age && camp.max_age ? ` Perfect for ages ${camp.min_age}-${camp.max_age}.` : ''} Find activities, pricing, and registration details.`}
        keywords={`summer camp ${camp.city} ${camp.state}, ${camp.name}, kids camp ${camp.city}, summer activities ${camp.state}, ${camp.camp_category_relations?.map((r: any) => r.camp_categories?.name).join(', ') || 'summer camp'}${camp.min_age && camp.max_age ? `, ages ${camp.min_age}-${camp.max_age}` : ''}`}
        url={`/camps/${camp.slug}`}
        geo={{
          placename: camp.city,
          region: camp.state,
          position: `${camp.latitude},${camp.longitude}`
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SummerCamp",
          "name": camp.name,
          "description": camp.short_description || camp.description,
          "url": `https://campwhiz.com/camps/${camp.slug}`,
          "telephone": camp.phone,
          "email": camp.email,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": camp.address,
            "addressLocality": camp.city,
            "addressRegion": camp.state,
            "postalCode": camp.zip_code
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": camp.latitude,
            "longitude": camp.longitude
          },
          "openingHours": camp.start_hour_1 && camp.finish_hour_1 ? [
            `Mo-Fr ${camp.start_hour_1}-${camp.finish_hour_1}`,
            camp.start_hour_2 && camp.finish_hour_2 ? `Mo-Fr ${camp.start_hour_2}-${camp.finish_hour_2}` : null
          ].filter(Boolean).join("; ") : undefined,
          "priceRange": camp.five_day_cost ? `$${camp.five_day_cost}` : undefined,
          "suitableForAge": camp.min_age && camp.max_age ? {
            "@type": "QuantitativeValue",
            "minValue": camp.min_age,
            "maxValue": camp.max_age
          } : undefined,
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock"
          }
        }}
      />
      <div className="page-content">
        {/* Admin Warning for Inactive Camps */}
        {isAdmin && !camp.active && (
          <WarningAlert 
            title="Inactive Camp - Admin View" 
            className="mb-6"
          >
            This camp is currently inactive and not visible to public users. Only admins can view this page.
          </WarningAlert>
        )}

        {/* Admin Actions Card */}
        {isAdmin && (
          <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-6" style={{ marginTop: '10px' }}>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Admin Actions</h3>
                  <p className="text-sm text-gray-500">Manage this camp listing</p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Preview Link Toggle */}
                  {camp && (
                    <>
                      <label className="flex items-center cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={!!camp.preview_token}
                            onChange={async (e) => {
                              const isChecked = e.target.checked;
                              try {
                                if (isChecked) {
                                  // Generate new 6-character random token
                                  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                                  let token = '';
                                  for (let i = 0; i < 6; i++) {
                                    token += chars.charAt(Math.floor(Math.random() * chars.length));
                                  }
                                  const { data, error } = await supabase
                                    .from('camps')
                                    .update({ preview_token: token })
                                    .eq('id', camp.id)
                                    .select();
                                  if (error) {
                                    const errorMsg = error.message || error.hint || error.code || 'Unknown error';
                                    toast.error(`Failed to enable preview link: ${errorMsg}`);
                                    console.error('Error updating preview token:', error);
                                    console.error('Error details:', JSON.stringify(error, null, 2));
                                    console.error('Error code:', error.code);
                                    console.error('Error hint:', error.hint);
                                    console.error('Camp ID:', camp.id);
                                    console.error('Token:', token);
                                    // Revert checkbox state on error
                                    e.target.checked = false;
                                  } else {
                                    toast.success('Preview link enabled');
                                    await queryClient.invalidateQueries(['camp', effectiveSlug, isAdmin]);
                                  }
                                } else {
                                  const { data, error } = await supabase
                                    .from('camps')
                                    .update({ preview_token: null })
                                    .eq('id', camp.id)
                                    .select();
                                  if (error) {
                                    const errorMsg = error.message || error.hint || error.code || 'Unknown error';
                                    toast.error(`Failed to disable preview link: ${errorMsg}`);
                                    console.error('Error clearing preview token:', error);
                                    console.error('Error details:', JSON.stringify(error, null, 2));
                                    console.error('Error code:', error.code);
                                    console.error('Error hint:', error.hint);
                                    console.error('Camp ID:', camp.id);
                                    // Revert checkbox state on error
                                    e.target.checked = true;
                                  } else {
                                    toast.success('Preview link disabled');
                                    await queryClient.invalidateQueries(['camp', effectiveSlug, isAdmin]);
                                  }
                                }
                              } catch (err) {
                                console.error('Unexpected error:', err);
                                toast.error('An unexpected error occurred');
                                // Revert checkbox state on error
                                e.target.checked = !isChecked;
                              }
                            }}
                          />
                          <div
                            className={`block w-12 h-7 rounded-full transition-colors duration-200 ${camp.preview_token ? "bg-primary" : "bg-gray-300"}`}
                          ></div>
                          <div
                            className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition transform duration-200 ${camp.preview_token ? "translate-x-5" : ""}`}
                          ></div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-700">Preview link</span>
                      </label>
                      {camp.preview_token && (
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/camps/${camp.slug}?preview=${camp.preview_token}`;
                            navigator.clipboard.writeText(url);
                            toast.success('Preview link copied to clipboard!');
                          }}
                          className="btn btn-secondary flex items-center gap-1.5"
                        >
                          <ClipboardIcon className="h-4 w-4" />
                          Copy Link
                        </button>
                      )}
                    </>
                  )}
                  {/* Preview Images Toggle */}
                  {camp && isAdmin && (
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={previewImagesEnabled}
                          onChange={(e) => {
                            if (!camp || !isAdmin) return;
                            const isChecked = e.target.checked;
                            const campIdString = String(camp.id); // Ensure string for consistent comparison
                            const newSet = new Set(previewImagesCamps);
                            if (isChecked) {
                              newSet.add(campIdString);
                            } else {
                              newSet.delete(campIdString);
                            }
                            setPreviewImagesCamps(newSet);
                            localStorage.setItem('admin_preview_images_camps', JSON.stringify(Array.from(newSet)));
                            // Dispatch custom event to notify other components
                            window.dispatchEvent(new Event('adminPreviewImagesChanged'));
                            // Force a re-render of camp cards by invalidating queries
                            queryClient.invalidateQueries(['camp']);
                            queryClient.invalidateQueries(['favorite-camps']);
                            queryClient.invalidateQueries(['proximity-camps']);
                            toast.success(isChecked ? `Preview images enabled for ${camp.name}` : `Preview images disabled for ${camp.name}`);
                          }}
                        />
                        <div
                          className={`block w-12 h-7 rounded-full transition-colors duration-200 ${previewImagesEnabled ? "bg-primary" : "bg-gray-300"}`}
                        ></div>
                        <div
                          className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition transform duration-200 ${previewImagesEnabled ? "translate-x-5" : ""}`}
                        ></div>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-700">Preview images</span>
                    </label>
                  )}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAdminActions(!showAdminActions);
                      }}
                      className="p-1.5 rounded-full border-2 border-primary hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
                      type="button"
                    >
                      <EllipsisVerticalIcon className="h-4 w-4 text-primary stroke-2" />
                    </button>
                  
                  {showAdminActions && (
                    <div 
                      className="absolute right-0 mt-2 w-56 rounded-lg shadow-2xl bg-white border border-gray-200 z-[100] overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                      style={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.1)' }}
                    >
                      <div className="py-2">
                        <button
                          onClick={handleEditCamp}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                          type="button"
                        >
                          <PencilSquareIcon className="h-4 w-4 mr-3 text-gray-500" />
                          Edit Camp
                        </button>
                        <button
                          onClick={() => {
                            setIsEnrichmentModalOpen(true);
                            setShowAdminActions(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                          type="button"
                        >
                          <SparklesIcon className="h-4 w-4 mr-3 text-green-500" />
                          AI Enrich
                        </button>
                        <button
                          onClick={() => {
                            setIsPasteEnrichmentModalOpen(true);
                            setShowAdminActions(false);
                          }}
                          className="w-full text-left px-4 py-2 text-xs text-gray-600 hover:bg-gray-50 flex items-center transition-colors ml-4 pl-4 border-l border-gray-200"
                          type="button"
                        >
                          <ClipboardIcon className="h-3.5 w-3.5 mr-2 text-gray-500" />
                          Paste Enrichment
                        </button>
                        <button
                          onClick={handleToggleActive}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          type="button"
                        >
                          {camp.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <div className="border-t border-gray-100 my-1"></div>
                        <button
                          onClick={handleDeleteCamp}
                          className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          type="button"
                        >
                          Delete Camp
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
        )}
        
        {/* Breadcrumb Navigation - Hidden on mobile */}
        <nav className="mb-8 hidden lg:block" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li>
              <a href="/" className="text-primary hover:text-primary/80 transition-colors">
                Home
              </a>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li>
              <a href="/camps-by-city" className="text-primary hover:text-primary/80 transition-colors">
                Camps by City
              </a>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li>
              <a 
                href={`/camps-by-city/${camp.state.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')}`}
                className="text-primary hover:text-primary/80 transition-colors"
              >
                {camp.state}
              </a>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li>
              <a 
                href={`/camps-by-city/${camp.state.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')}/${camp.city.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')}`}
                className="text-primary hover:text-primary/80 transition-colors"
              >
                {camp.city}
              </a>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-gray-900 font-medium">{camp.name}</li>
          </ol>
        </nav>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          {/* Left Column */}
          <div className="lg:col-span-2">
            {/* Main Camp Image - Only show if there are photos AND (camp is paying OR has valid preview token) */}
            {photoImages.length > 0 && shouldShowPremiumSections && (
              <div className="mb-8">
                <div className="w-full h-72 md:h-96 rounded-2xl shadow-lg overflow-hidden bg-gray-200 flex items-center justify-center">
                  <RotatingCampImages images={photoImages} className="w-full h-full object-cover" />
                </div>
              </div>
            )}

            {/* Main Content */}
            <div className="space-y-8">
              {/* Quick Info */}
              <div className="bg-white rounded-2xl shadow relative">
                {/* Main content */}
                <div className="p-8">
                  {/* City, State and icons row */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                    <p className="text-base sm:text-lg text-gray-600 leading-tight">{camp.city}, {camp.state}</p>
                    {/* Action icons - right side with grey circle backgrounds */}
                    <div className="flex items-center gap-2">
                      {/* Flag icon - inactive */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.info('Report camp feature coming soon');
                        }}
                        className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors opacity-60 cursor-not-allowed"
                        aria-label="Report camp"
                        title="Report camp (coming soon)"
                        disabled
                      >
                        <FlagIcon className="h-4 w-4 text-gray-400" strokeWidth={1.5} />
                      </button>
                      {/* Favorite icon */}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (isUpdatingFavorite) return;

                          try {
                            setIsUpdatingFavorite(true);
                            const isNowFavorite = await api.camps.toggleFavorite(camp.id);
                            setIsFavorite(isNowFavorite);
                            if (isNowFavorite) {
                              track('favorite_added', { camp_id: camp.id });
                            } else {
                              track('favorite_removed', { camp_id: camp.id, persona: 'parent' });
                            }
                            // Invalidate favorites query cache
                            queryClient.invalidateQueries(['favorite-camps']);
                            toast.success(isNowFavorite ? 'Added to favorites' : 'Removed from favorites');
                          } catch (error) {
                            toast.error('Failed to update favorite status');
                          } finally {
                            setIsUpdatingFavorite(false);
                          }
                        }}
                        disabled={isUpdatingFavorite}
                        className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {isFavorite ? (
                          <HeartIconSolid className="h-4 w-4 text-primary fill-primary" />
                        ) : (
                          <HeartIcon className="h-4 w-4 text-primary" strokeWidth={1.5} />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Camp Name and Logo row */}
                  <div className="flex items-start justify-between mb-6">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 leading-tight flex-1">{camp.name}</h1>
                    {/* Logo on right */}
                    {logoImage && shouldShowPremiumSections && (
                      <div className="ml-4 flex-shrink-0">
                        <img
                          src={logoImage}
                          alt={`${camp.name} logo`}
                          className="h-16 object-contain"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Divider above Camp Details - already included in City/State row */}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-4">Camp Details</h2>
                    <div className="space-y-4">
                      {camp.short_description && (
                        <p className="text-gray-600 whitespace-pre-wrap">{camp.short_description}</p>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <UserGroupIcon className="h-5 w-5" />
                        <span>Ages {camp.min_age}-{camp.max_age}</span>
                      </div>
                      {camp.camp_category_relations && camp.camp_category_relations.length > 0 && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <CategoryTags 
                            categories={camp.camp_category_relations.map((relation: any) => relation.camp_categories).filter(Boolean)} 
                            maxTags={6}
                            className=""
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
                    <div className="space-y-3">
                      {(camp.address || camp.city) && (
                        <div className="flex items-start gap-2 text-gray-600">
                          <MapPinIcon className="h-5 w-5 mt-0.5" />
                          <div>
                            {camp.address && (
                              <div>{extractStreetAddress(camp.address, camp.city, camp.state, camp.zip_code)}</div>
                            )}
                            {(camp.city || camp.state || camp.zip_code) && (
                              <div>{[camp.city, camp.state, camp.zip_code].filter(Boolean).join(', ')}</div>
                            )}
                          </div>
                        </div>
                      )}
                      {camp.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <PhoneIcon className="h-5 w-5" />
                          <a href={`tel:${camp.phone}`} className="text-primary hover:text-primary/80">{camp.phone}</a>
                        </div>
                      )}
                      {camp.email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <EnvelopeIcon className="h-5 w-5" />
                          <a href={`mailto:${camp.email}`} className="text-primary hover:text-primary/80">{camp.email}</a>
                        </div>
                      )}
                      {camp.website_url && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <GlobeAltIcon className="h-5 w-5" />
                          <a href={addUtmParams(camp.website_url)} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5 group transition-colors">
                            <span>Visit Website</span>
                            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </a>
                        </div>
                      )}
                      {camp.social_media?.linkedin && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <GlobeAltIcon className="h-5 w-5" />
                          <a 
                            href={addUtmParams(camp.social_media.linkedin)} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5 group transition-colors"
                          >
                            <span>LinkedIn</span>
                            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </a>
                        </div>
                      )}
                      {/* Social Media Links - Side by Side */}
                      {camp.social_media && (
                        (camp.social_media.facebook || camp.social_media.instagram || camp.social_media.twitter || camp.social_media.tiktok) && (
                          <div className="flex items-center gap-3">
                            {camp.social_media.facebook && (
                              <a 
                                href={addUtmParams(camp.social_media.facebook)} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-primary hover:opacity-70 transition-opacity"
                                aria-label="Facebook"
                              >
                                <img 
                                  src="https://cdn.simpleicons.org/facebook" 
                                  alt="" 
                                  className="h-5 w-5 object-contain" 
                                  loading="lazy"
                                />
                              </a>
                            )}
                            {camp.social_media.instagram && (
                              <a 
                                href={addUtmParams(camp.social_media.instagram)} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-primary hover:opacity-70 transition-opacity"
                                aria-label="Instagram"
                              >
                                <img 
                                  src="https://cdn.simpleicons.org/instagram" 
                                  alt="" 
                                  className="h-5 w-5 object-contain" 
                                  loading="lazy"
                                />
                              </a>
                            )}
                            {camp.social_media.twitter && (
                              <a 
                                href={addUtmParams(camp.social_media.twitter)} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-primary hover:opacity-70 transition-opacity"
                                aria-label="X"
                              >
                                <img 
                                  src="https://cdn.simpleicons.org/x" 
                                  alt="" 
                                  className="h-5 w-5 object-contain" 
                                  loading="lazy"
                                />
                              </a>
                            )}
                            {camp.social_media.tiktok && (
                              <a 
                                href={addUtmParams(camp.social_media.tiktok)} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-primary hover:opacity-70 transition-opacity"
                                aria-label="TikTok"
                              >
                                <img 
                                  src="https://cdn.simpleicons.org/tiktok" 
                                  alt="" 
                                  className="h-5 w-5 object-contain" 
                                  loading="lazy"
                                />
                              </a>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
                </div>
              </div>

              {/* Activities and Amenities */}
              {(camp.activities?.length > 0 || camp.amenities?.length > 0) && (
                <div className="bg-white rounded-2xl shadow p-8">
                  <h2 className="text-xl font-semibold mb-4">Activities & Amenities</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Activities */}
                    {camp.activities?.length > 0 && (
                      <div>
                        <h3 className="font-medium mb-3 text-blue-600">Activities</h3>
                        <div className="flex flex-wrap gap-2">
                          {camp.activities.map((activity: string, index: number) => (
                            <span key={index} className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                              {activity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Amenities */}
                    {camp.amenities?.length > 0 && (
                      <div>
                        <h3 className="font-medium mb-3 text-green-600">Amenities</h3>
                        <div className="flex flex-wrap gap-2">
                          {camp.amenities.map((amenity: string, index: number) => (
                            <span key={index} className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                              {amenity}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Schedule & Pricing - Only show if (camp is paying OR has valid preview token) */}
              {shouldShowPremiumSections && (
                <div className="bg-white rounded-2xl shadow p-8">
                  <h2 className="text-xl font-semibold mb-4">Schedule & Pricing</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-medium mb-3">Hours</h3>
                      <div className="space-y-3">
                        {camp.operating_hours_other ? (
                          <div className="text-gray-600">
                            {camp.operating_hours_other}
                          </div>
                        ) : (
                          <>
                            {camp.start_hour_1 && camp.finish_hour_1 && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <CalendarDaysIcon className="h-5 w-5" />
                                <span>Regular Hours: {camp.start_hour_1} - {camp.finish_hour_1}</span>
                              </div>
                            )}
                            {camp.start_hour_2 && camp.finish_hour_2 && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <CalendarDaysIcon className="h-5 w-5" />
                                <span>Extended Hours: {camp.start_hour_2} - {camp.finish_hour_2}</span>
                              </div>
                            )}
                            {camp.extended_time_available && (
                              <div className="text-sm text-gray-500">
                                Extended time available upon request
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium mb-3">Pricing & Meals</h3>
                      <div className="space-y-3">
                        {camp.five_day_cost && (
                          <div className="text-gray-600">
                            <span className="font-medium">Weekly Rate:</span>{' '}
                            {(() => {
                              // Check if it's a numeric value (can be parsed as number)
                              const numericValue = parseFloat(camp.five_day_cost);
                              const isNumeric = !isNaN(numericValue) && isFinite(numericValue);
                              // Only add $ if it's numeric, otherwise display as-is
                              return isNumeric ? `$${camp.five_day_cost}` : camp.five_day_cost;
                            })()}
                          </div>
                        )}
                        {camp.lunch_provided && (
                          <div className="text-gray-600">
                            <span className="font-medium">Lunch:</span> Provided
                            {camp.lunch_fee && ` (Additional $${camp.lunch_fee})`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Additional Details */}
                  {camp.schedule_pricing_details && (
                    <div className="mt-6">
                      <h3 className="font-medium mb-3">Additional Details</h3>
                      <div className="text-gray-600">
                        <MarkdownRenderer content={camp.schedule_pricing_details} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dates Available - Only show if (camp is paying OR has valid preview token) */}
              {shouldShowPremiumSections && camp.dates_available && camp.dates_available.length > 0 && (
                <div className="bg-white rounded-2xl shadow p-8">
                  <h2 className="text-xl font-semibold mb-4">Available Dates</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {camp.dates_available.map((date: string, index: number) => (
                      <div key={index} className="bg-gray-50 p-3 rounded text-center">
                        {date}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Description */}
              {camp.description && (
                <div className="bg-white rounded-2xl shadow p-8">
                  <h2 className="text-xl font-semibold mb-4">About This Camp</h2>
                  <div>
                    {/* Debug: Log description to see what we're rendering */}
                    {process.env.NODE_ENV === 'development' && console.log('[CampPage] Rendering description:', camp.description.substring(0, 300), 'Has headings:', /^#+\s/m.test(camp.description) || camp.description.includes('\n#'))}
                    <MarkdownRenderer content={camp.description} />
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {camp.notes && typeof camp.notes === 'string' && camp.notes.trim() && camp.notes !== '{}' && (() => {
                // Filter out JSON patterns (Google Places API format)
                const jsonPatterns = [
                  /^\s*\{[\s\S]*"Service options"[\s\S]*\}/,
                  /^\s*\{[\s\S]*"Highlights"[\s\S]*\}/,
                  /^\s*\{[\s\S]*"Accessibility"[\s\S]*\}/,
                  /^\s*\{[\s\S]*"Amenities"[\s\S]*\}/,
                  /^\s*\{[\s\S]*"Children"[\s\S]*\}/,
                  /^\s*\{[\s\S]*"Pets"[\s\S]*\}/,
                ];
                
                const isJSON = jsonPatterns.some(pattern => pattern.test(camp.notes)) ||
                              (/^\s*[{\[]/.test(camp.notes) && /[}\]]\s*$/.test(camp.notes));
                
                // Don't display if it's JSON
                if (isJSON) {
                  try {
                    JSON.parse(camp.notes);
                    return null; // Valid JSON - don't display
                  } catch {
                    // Not valid JSON, might be partial - still filter it out if it looks like JSON
                    if (jsonPatterns.some(pattern => pattern.test(camp.notes))) {
                      return null;
                    }
                  }
                }
                
                return (
                  <div className="bg-white rounded-2xl shadow p-8">
                    <h2 className="text-xl font-semibold mb-4">Additional Information</h2>
                    <div className="prose max-w-none text-gray-600 whitespace-pre-wrap">
                      {camp.notes}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Right Column (Sidebar) */}
          <div className="lg:col-span-1 sticky top-24 self-start">
            {/* Sidebar order: 
                - For parents/dual-role: Calendar -> Map -> Claim (if owner too)
                - For owners only: Claim/Dashboard -> Map
                - For unlogged: Calendar teaser -> Map -> Claim
            */}
            
            {/* Claim Camp Card - Show FIRST for camp-owner-only users */}
            {(isCampOwner && !isParent) && (
              <div className="mb-8">
                <ClaimCampCard 
                  camp={camp as ClaimableCamp} 
                  currentUserId={session?.user?.id}
                  onEditClick={handleEditCamp}
                />
              </div>
            )}
            
            {/* Add to Calendar Card - Show for parents, dual-role users, or unlogged */}
            {(isParent || !isCampOwner) && (
              <div 
                className="relative rounded-2xl shadow-lg mb-8 overflow-hidden"
                style={{
                  padding: '1px',
                  background: `linear-gradient(to right, ${currentTheme.primary}, ${currentTheme.secondary})`,
                }}
              >
                <div className="relative bg-white rounded-2xl p-8 overflow-hidden">
                  {/* Animated gradient blur background */}
                  <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                    <div 
                      className="absolute top-1/2 left-1/2 w-[150%] h-[150%] bg-gradient-to-br from-white/40 via-blue-100/20 to-purple-100/15 blur-3xl calendar-teaser-gradient-1"
                      style={{ transformOrigin: 'center center' }}
                    ></div>
                    <div 
                      className="absolute top-1/2 left-1/2 w-[150%] h-[150%] bg-gradient-to-tl from-white/40 via-purple-100/15 to-blue-100/20 blur-3xl calendar-teaser-gradient-2"
                      style={{ transformOrigin: 'center center' }}
                    ></div>
                  </div>
                {!session ? (
                  <div className="text-center relative z-10">
                    <div className="mb-4">
                      <CalendarDaysIcon className="h-12 w-12 text-primary mx-auto mb-3" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-3">
                      Camp season, simplified!
                    </h2>
                    <p className="text-gray-700 mb-4">
                      Parent,{' '}
                      <Link to="/register" className="text-primary hover:text-primary/80 font-medium underline decoration-2 underline-offset-2">
                        create a FREE account
                      </Link>
                      {' '}to save camps, build your family calendar, and stay on top of every schedule.
                    </p>
                    <ul className="text-sm text-gray-600 mb-4 space-y-2 text-left max-w-xs mx-auto">
                      <li className="flex items-start gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>Save camps you're considering <span className="text-gray-500">(free)</span></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>Track schedules for multiple kids <span className="text-gray-500">(free)</span></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>Sync with Google Calendar or iCal <span className="text-gray-500">($6/mo)</span></span>
                      </li>
                    </ul>
                    <p className="text-sm text-gray-600 font-medium">
                      Start free. Upgrade anytime.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleAddToCalendar} className="space-y-4">
                    <div>
                      <label htmlFor="child" className="block text-sm font-medium text-gray-700 mb-1">
                        Select Child
                      </label>
                      <div className="relative">
                        <select
                          id="child"
                          value={selectedChild}
                          onChange={(e) => setSelectedChild(e.target.value)}
                          className="form-field w-full px-3 py-2 pr-10 rounded-lg border border-gray-300 shadow-sm focus:outline-none appearance-none"
                          required
                        >
                          <option value="">Select a child</option>
                          {children.map((child) => (
                            <option key={child.id} value={child.id}>
                              {child.name}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg className="h-5 w-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        id="start-date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          // If end date is before new start date, clear it
                          if (endDate && e.target.value && endDate < e.target.value) {
                            setEndDate('');
                          }
                        }}
                        className="form-field w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:outline-none bg-white"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        id="end-date"
                        value={endDate}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          // Validate that end date is not before start date
                          if (startDate && selectedDate && selectedDate < startDate) {
                            // Don't allow selection of dates before start date
                            return;
                          }
                          setEndDate(selectedDate);
                        }}
                        min={startDate || undefined}
                        className="form-field w-full px-3 py-2 rounded-lg border border-gray-300 shadow-sm focus:outline-none bg-white"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary w-full inline-flex justify-center items-center"
                    >
                      <CalendarDaysIcon className="h-5 w-5 mr-2" />
                      Add to Calendar
                    </button>
                  </form>
                )}
                </div>
              </div>
            )}

            {/* Map */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-xl font-semibold mb-4">Location</h2>
              <div className="h-64 rounded-lg overflow-hidden">
                <CampMap
                  camps={[camp]}
                  center={[camp.latitude, camp.longitude]}
                  zoom={13}
                  onCampSelect={() => {}}
                  userLocation={null}
                  radius={null}
                />
              </div>
            </div>

            {/* Claim Camp Card - Show for:
                - Unlogged users (can claim or see claimed message)
                - Dual-role users (can claim other camps)
                - Parents only (can see claimed message for trust/transparency)
                Owner-only users see it at top */}
            {((!session) || isParent || (isCampOwner && isParent)) && (
              <div className="mb-8">
                <ClaimCampCard 
                  camp={camp as ClaimableCamp} 
                  currentUserId={session?.user?.id}
                  onEditClick={handleEditCamp}
                />
              </div>
            )}

            {/* Purchase Teaser - Hidden for now, keeping for reference */}
            <div className="hidden">
              <PurchaseTeaser 
                camp={camp as ClaimableCamp} 
                currentUserId={session?.user?.id}
              />
            </div>

          </div>
        </div>
      </div>

      {/* Edit Camp Modal */}
      {isEditModalOpen && (
        <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div></div>}>
          <AdminCampOverlay
            camp={camp}
            isOpen={isEditModalOpen}
            isAdmin={!!isAdmin}
            onClose={() => setIsEditModalOpen(false)}
            onSave={(updatedCamp) => {
              // Invalidate all camp-related queries to handle slug changes
              void queryClient.invalidateQueries(['camp']);
              void queryClient.invalidateQueries(['admin-camps']);

              // If slug changed, redirect to new URL
              if (updatedCamp && updatedCamp.slug && updatedCamp.slug !== effectiveSlug) {
                navigate(`/camps/${updatedCamp.slug}`, { replace: true });
              }

              setIsEditModalOpen(false);
            }}
            onDelete={() => {
              setIsEditModalOpen(false);
              handleDeleteCamp();
            }}
          />
        </Suspense>
      )}

      {/* Delete Confirmation Modal */}
      <Transition.Root show={!!deleteConfirmation} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setDeleteConfirmation(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Remove Camp
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to remove{' '}
                          <span className="font-medium">{deleteConfirmation?.camp.name}</span>{' '}
                          from your camp listings? <span className="font-semibold text-gray-700">This action cannot be undone.</span>
                        </p>
                        
                        {/* Skip Forever Checkbox */}
                        <div className="mt-4">
                          <label className="flex items-start">
                            <input
                              type="checkbox"
                              checked={deleteConfirmation?.skipForever || false}
                              onChange={(e) => {
                                if (deleteConfirmation) {
                                  setDeleteConfirmation({
                                    ...deleteConfirmation,
                                    skipForever: e.target.checked
                                  });
                                }
                              }}
                              className="mt-0.5 h-4 w-4 text-primary border-gray-300 rounded focus-visible:ring-primary"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Skip future imports of this camp to avoid re-processing and AI credits
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="btn-danger sm:ml-3 sm:w-auto"
                      onClick={handleDeleteConfirm}
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      className="btn-outline mt-3 sm:mt-0 sm:w-auto"
                      onClick={() => setDeleteConfirmation(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Payment Success Modal */}
      {showPaymentSuccess && (
        <PaymentSuccessModal
          planName={successPlanName}
          onDismiss={() => setShowPaymentSuccess(false)}
          campId={camp?.id}
          campSlug={camp?.slug}
        />
      )}

      {/* Enrichment Modals */}
      {camp && (
        <>
          <EnrichmentModal
            isOpen={isEnrichmentModalOpen}
            onClose={() => setIsEnrichmentModalOpen(false)}
            campId={camp.id}
            campName={camp.name}
            currentWebsite={camp.website_url || undefined}
            onSuccess={() => {
              queryClient.invalidateQueries(['camp', effectiveSlug]);
              toast.success('Camp enriched successfully!');
              setIsEnrichmentModalOpen(false);
            }}
          />
          <PasteEnrichmentModal
            isOpen={isPasteEnrichmentModalOpen}
            onClose={() => setIsPasteEnrichmentModalOpen(false)}
            campId={camp.id}
            campName={camp.name}
            currentWebsite={camp.website_url || undefined}
            onSuccess={() => {
              queryClient.invalidateQueries(['camp', effectiveSlug]);
              toast.success('Camp enriched successfully!');
              setIsPasteEnrichmentModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
} 